import { ContactIcon, PlusIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import { useMutation } from '@tanstack/react-query'
import React from 'react'
import { toast } from 'sonner'

import { API } from '../../../api/apis.config'
import { createTripProviderContact } from '../../../api/trip-providers/create-trip-provider-contact'
import { updateTripProviderContact } from '../../../api/trip-providers/update-trip-provider-contact'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { TripProviderContact } from '../../../lib/types/models/trip-provider/trip-provider-contact'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { formatPhoneNumber } from '../../../utils/functions/format-phone-number'
import { DynamicTableRef } from '../../elements/dynamic-table'
import NestedDataTable from '../../elements/nested-table'
import TripProviderContactForm from './trip-provider-contact-form'

type Props = {
  tripProviderId: number
}

const TripProviderContactDataTable: React.FC<Props> = ({ tripProviderId }) => {
  const { selectedData, modalState, toggleModal, closeModel } = useModalManager<
    'create' | 'edit',
    TripProviderContact
  >(['create', 'edit'])
  const tableRef = React.useRef<DynamicTableRef<TripProviderContact>>(null)

  const { mutateAsync: createTripProviderContactMutation } = useMutation({
    mutationFn: createTripProviderContact,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      closeModel('create')
      toast.success('Contact créé avec succès')
    }
  })

  const { mutateAsync: updateTripProviderContactMutation } = useMutation({
    mutationFn: updateTripProviderContact,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      closeModel('edit')
      toast.success('Contact modifié avec succès')
    }
  })

  return (
    <div>
      <NestedDataTable<TripProviderContact, null>
        ref={tableRef}
        variant="action"
        title={'Contact'}
        deleteMessage={{
          name: 'contact',
          attributeName: 'firstName',
          deleteOne: 'Êtes-vous sûr de vouloir supprimer ce contact ?',
          deleteMany: 'Êtes-vous sûr de vouloir supprimer ces contacts ?',
          deleteAll: 'Êtes-vous sûr de vouloir supprimer tous les contacts ?'
        }}
        columns={[
          {
            accessorKey: 'position',
            header: 'Fonction'
          },
          {
            accessorKey: 'lastName',
            header: 'NOM'
          },
          {
            accessorKey: 'firstName',
            header: 'Prénom'
          },
          {
            accessorKey: 'email',
            header: 'Email'
          },
          {
            accessorKey: 'phoneNumber',
            header: 'Numéro de téléphone',
            cell: ({ row }) => formatPhoneNumber(row.original.phoneNumber)
          }
        ]}
        onEditAction={(tripProvider) => toggleModal('edit', tripProvider)}
        endpoint={API.tripProviders.getContacts.url.replace(':id', tripProviderId.toString())}
        actions={[
          {
            type: 'global',
            icon: PlusIcon,
            refetch: true,
            variant: 'outline',
            label: 'Ajouter un nouveau contact',
            fn: () => toggleModal('create')
          }
        ]}
      />

      <Modal
        isOpen={modalState.create}
        title={'Ajouter un contact'}
        icon={ContactIcon}
        hideActions
        size="medium"
        onCancel={() => closeModel('create')}
      >
        <TripProviderContactForm
          className={MODAL_BODY_CLASSNAME}
          buttonsConfig={modalButtonConfig({
            onCancel: () => closeModel('create'),
            onConfirm: (data) => createTripProviderContactMutation({ tripProviderId, ...data })
          })}
        />
      </Modal>

      {selectedData && (
        <Modal
          isOpen={modalState.edit}
          title={'Modifier un contact'}
          icon={ContactIcon}
          hideActions
          onCancel={() => closeModel('edit')}
        >
          <TripProviderContactForm
            className={MODAL_BODY_CLASSNAME}
            defaultValues={selectedData}
            buttonsConfig={modalButtonConfig({
              onCancel: () => closeModel('edit'),
              onConfirm: (data) =>
                updateTripProviderContactMutation({
                  id: selectedData.id,
                  tripProviderId,
                  ...data
                })
            })}
          />
        </Modal>
      )}
    </div>
  )
}

export default TripProviderContactDataTable
